import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FileText, User, CreditCard, DollarSign } from 'lucide-react';
import { getPaymentStatusColorValue } from '../../styles/tokens/colors';
import { fmtFechaHoraDb } from '../../lib/fechas';

const PaymentDetailModal = ({ payment, isOpen, onClose }) => {
  const navigate = useNavigate();
  if (!payment) return null;

  const getMetadataObject = (metadataValue) => {
    if (!metadataValue) return null;
    if (typeof metadataValue === 'string') {
      try {
        return JSON.parse(metadataValue);
      } catch {
        return null;
      }
    }
    if (typeof metadataValue === 'object') return metadataValue;
    return null;
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const normalized = value.replace(/\./g, '').replace(',', '.').trim();
      const parsed = Number(normalized);
      if (!Number.isFinite(parsed)) return null;
      return parsed;
    }
    return null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const formatCLP = (amount) => {
    if (amount === null || amount === undefined) return 'No disponible';
    const numeric = typeof amount === 'number' ? amount : parseNumber(amount);
    if (numeric === null) return 'No disponible';
    return formatCurrency(numeric);
  };

  // `pagos.transaction_date` no trae zona (`timestamp without time zone`), así
  // que el instante se normaliza antes de formatear; sin eso el pago se mostraba
  // 3 o 4 horas tarde, a veces ya al día siguiente. `fmtFechaHoraDb` rinde en
  // hora de Chile.
  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return fmtFechaHoraDb(dateString, { month: 'long', day: 'numeric' });
  };

  const getPaymentMethodName = (code) => {
    const methods = {
      'webpay': 'Webpay Plus',
      'webpaypatpass': 'Webpay PatPass',
      'mercadopago': 'MercadoPago',
      'flow': 'Flow',
      'virtualpos': 'VirtualPOS',
      'transfer': 'Transferencia',
      'cash': 'Efectivo'
    };
    return methods[code] || code;
  };

  const getStatusBadge = (status) => {
    const statusColors = getPaymentStatusColorValue(status);
    return (
      <Badge 
        className="px-2 py-1"
        style={{
          backgroundColor: statusColors.background,
          borderColor: statusColors.border,
          color: statusColors.text
        }}
      >
        {status}
      </Badge>
    );
  };

  const getTitularName = (datosTitular) => {
    if (!datosTitular) return 'No disponible';
    if (typeof datosTitular === 'string') {
      try {
        const parsed = JSON.parse(datosTitular);
        return `${parsed.nombre || ''} ${parsed.apellido_paterno || ''} ${parsed.apellido_materno || ''}`.trim();
      } catch {
        return datosTitular;
      }
    }
    return `${datosTitular.nombre || ''} ${datosTitular.apellido_paterno || ''} ${datosTitular.apellido_materno || ''}`.trim();
  };

  const paymentMetadata = getMetadataObject(
    payment?.payment_metadata ?? payment?.provider_metadata ?? payment?.charges_program
  );
  const virtualposCharge = paymentMetadata?.charge || null;
  const virtualposOrder = virtualposCharge?.payment?.order || null;
  const virtualposDeposits = Array.isArray(virtualposOrder?.deposits) ? virtualposOrder.deposits : [];
  const firstDeposit = virtualposDeposits?.[0] || null;
  const computedFeesCLP = (() => {
    const processingFee = parseNumber(firstDeposit?.processing_fee);
    const flatFee = parseNumber(firstDeposit?.processing_flat_fee);
    if (processingFee === null && flatFee === null) return null;
    return (processingFee || 0) + (flatFee || 0);
  })();
  const computedChargedCLP = parseNumber(virtualposOrder?.amount);
  const computedNetCLP = parseNumber(firstDeposit?.payout_amount);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden bg-white">
        <div className="flex flex-col max-h-[90vh]">
          <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white px-6 py-5">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="flex items-center gap-2 text-white">
                    <CreditCard className="h-5 w-5" />
                    Detalle del Pago #{payment.id}
                  </DialogTitle>
                  <p className="text-sm text-slate-200 mt-1">
                    Trazabilidad y datos del gateway
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {payment?.status ? getStatusBadge(payment.status) : null}
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto bg-white px-6 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Información del Pago */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Información del Pago
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Monto</label>
                  <p className="text-lg font-semibold">{formatCurrency(payment.amount)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Estado</label>
                  <div className="mt-1">{getStatusBadge(payment.status)}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Método de Pago</label>
                  <p className="font-medium">{getPaymentMethodName(payment.payment_type_code)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fecha de Transacción</label>
                  <p className="font-medium">{formatDate(payment.transaction_date)}</p>
                </div>
              </div>

              {payment.authorization_code && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Código de Autorización</label>
                  <p className="font-mono text-sm bg-muted p-2 rounded">{payment.authorization_code}</p>
                </div>
              )}

              {payment.session_id && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">ID de Sesión</label>
                  <p className="font-mono text-sm bg-muted p-2 rounded">{payment.session_id}</p>
                </div>
              )}

              {payment.is_recurring && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-blue-600 border-blue-200">
                    Pago Recurrente
                  </Badge>
                  {payment.next_payment_date && (
                    <span className="text-sm text-muted-foreground">
                      Próximo: {formatDate(payment.next_payment_date)}
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Información del Cliente */}
          {payment.contratacion && (
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Información del Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Titular</label>
                  <p className="font-medium">{getTitularName(payment.contratacion.datos_titular)}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">RUT Titular</label>
                    <p className="font-medium">{payment.contratacion.rut_titular || 'No disponible'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="font-medium">{payment.contratacion.mail || 'No disponible'}</p>
                  </div>
                </div>

                {payment.contratacion.cotizaciones && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Plan Contratado</label>
                    <p className="font-medium">
                      {payment.contratacion.cotizaciones.planes?.nombre || 'No disponible'}
                    </p>
                    {payment.contratacion.cotizaciones.planes?.uf_base && (
                      <p className="text-sm text-muted-foreground">
                        Valor base: {payment.contratacion.cotizaciones.planes.uf_base} UF
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Trazabilidad - Orden de Pago */}
          {payment.orden_pago && (
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Orden de Pago Relacionada
                  </CardTitle>
                  {payment.orden_pago?.id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/ordenes-pago/${payment.orden_pago.id}`)}
                    >
                      Ver
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">ID Orden</label>
                    <p className="font-mono text-sm">{payment.orden_pago.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Monto Orden</label>
                    <p className="font-semibold">{formatCurrency(payment.orden_pago.monto)}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Estado Orden</label>
                    <div className="mt-1">{getStatusBadge(payment.orden_pago.estado)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Fecha Creación</label>
                    <p className="font-medium">{formatDate(payment.orden_pago.created_at)}</p>
                  </div>
                </div>

                {payment.orden_pago.descripcion && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Descripción</label>
                    <p className="text-sm bg-muted p-2 rounded">{payment.orden_pago.descripcion}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {paymentMetadata && (
            <Card className="shadow-sm border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Trazabilidad del Pago (Metadata)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Proveedor</label>
                    <p className="font-medium">{payment?.payment_provider || 'No disponible'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Tipo Tarjeta</label>
                    <p className="font-medium">{payment?.card_type || 'No disponible'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Últimos 4</label>
                    <p className="font-mono text-sm">{payment?.card_last_four || 'No disponible'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Código Respuesta</label>
                    <p className="font-medium">{payment?.response_code ?? 'No disponible'}</p>
                  </div>
                </div>

                {virtualposCharge && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Charge ID</label>
                        <p className="font-mono text-sm break-all">{virtualposCharge?.id || 'No disponible'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Suscripción</label>
                        <p className="font-mono text-sm break-all">
                          {paymentMetadata?.suscription_id || paymentMetadata?.subscription_id || 'No disponible'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Estado Charge</label>
                        <div className="mt-1">{getStatusBadge(virtualposCharge?.status || 'No disponible')}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Fecha Charge</label>
                        <p className="font-medium">{formatDate(virtualposCharge?.charge_date)}</p>
                      </div>
                    </div>

                    {virtualposOrder && (
                      <div className="rounded-md border p-3 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Orden UUID</label>
                            <p className="font-mono text-sm break-all">{virtualposOrder?.uuid || 'No disponible'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Estado Orden</label>
                            <div className="mt-1">{getStatusBadge(virtualposOrder?.status || 'No disponible')}</div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Cobrado (CLP)</label>
                            <p className="font-semibold">{formatCLP(computedChargedCLP)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Autorizado</label>
                            <p className="font-medium">
                              {formatDate(virtualposOrder?.authorized_at || virtualposOrder?.created_at)}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Auth Code</label>
                            <p className="font-mono text-sm">{virtualposOrder?.auth_code || 'No disponible'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Payment Type</label>
                            <p className="font-medium">{virtualposOrder?.payment_type_code || 'No disponible'}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Recaudado (CLP)</label>
                            <p className="font-semibold">{formatCLP(computedNetCLP)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Fees/Comisión (CLP)</label>
                            <p className="font-semibold">{formatCLP(computedFeesCLP)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Depósito</label>
                            <p className="font-medium">{firstDeposit?.payout_date ? formatDate(firstDeposit.payout_date) : 'No disponible'}</p>
                          </div>
                        </div>

                        {virtualposDeposits.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-muted-foreground">Depósitos ({virtualposDeposits.length})</div>
                            <div className="rounded-md border overflow-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-muted">
                                  <tr>
                                    <th className="text-left p-2 font-medium">Descripción</th>
                                    <th className="text-left p-2 font-medium">Fecha</th>
                                    <th className="text-left p-2 font-medium">Monto</th>
                                    <th className="text-left p-2 font-medium">Fee</th>
                                    <th className="text-left p-2 font-medium">Fee Fijo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {virtualposDeposits.map((deposit, idx) => (
                                    <tr key={`${deposit?.payout_date || ''}-${idx}`} className="border-t">
                                      <td className="p-2">{deposit?.description || '-'}</td>
                                      <td className="p-2">{deposit?.payout_date ? formatDate(deposit.payout_date) : '-'}</td>
                                      <td className="p-2">{formatCLP(deposit?.payout_amount)}</td>
                                      <td className="p-2">{formatCLP(deposit?.processing_fee)}</td>
                                      <td className="p-2">{formatCLP(deposit?.processing_flat_fee)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {virtualposCharge?.payment?.client && (
                      <div className="rounded-md border p-3 space-y-2">
                        <div className="text-sm font-medium text-muted-foreground">Cliente (Gateway)</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Nombre</label>
                            <p className="font-medium">
                              {`${virtualposCharge?.payment?.client?.first_name || ''} ${virtualposCharge?.payment?.client?.last_name || ''}`.trim() ||
                                'No disponible'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">RUT</label>
                            <p className="font-medium">{virtualposCharge?.payment?.client?.social_id || 'No disponible'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Email</label>
                            <p className="font-medium">{virtualposCharge?.payment?.client?.email || 'No disponible'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Teléfono</label>
                            <p className="font-medium">{virtualposCharge?.payment?.client?.phone || 'No disponible'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {virtualposCharge?.payment?.commerce && (
                      <div className="rounded-md border p-3 space-y-2">
                        <div className="text-sm font-medium text-muted-foreground">Comercio (Gateway)</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Nombre</label>
                            <p className="font-medium">{virtualposCharge?.payment?.commerce?.name || 'No disponible'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Commerce ID</label>
                            <p className="font-medium">{virtualposCharge?.payment?.commerce?.commerce_id || 'No disponible'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                  <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 text-xs font-medium text-slate-700">
                    JSON completo
                  </div>
                  <div className="bg-slate-950 text-slate-100 p-3 overflow-auto max-h-[50vh]">
                    <pre className="text-xs whitespace-pre-wrap break-words">
                    {JSON.stringify(paymentMetadata, null, 2)}
                  </pre>
                </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Certificados Relacionados */}
          {payment.certificados && payment.certificados.length > 0 && (
            <Card className="shadow-sm border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Certificados Relacionados ({payment.certificados.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {payment.certificados.map((certificado) => (
                    <div key={certificado.id} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Certificado #{certificado.id}</p>
                          <p className="text-sm text-muted-foreground">
                            Emitido: {formatDate(certificado.fecha_emision)}
                          </p>
                          {certificado.archivo_id && (
                            <p className="text-xs text-muted-foreground">
                              Archivo ID: <span className="font-mono">{certificado.archivo_id}</span>
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
            </div>
          </div>

          <div className="bg-white border-t border-slate-200 px-6 py-4">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDetailModal;
